import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import type { Idea } from '@/lib/types';

interface GeneratedTask {
  title: string;
  description: string;
  task_type: 'experiment' | 'build' | 'research' | 'outreach' | 'admin';
  priority: 'low' | 'medium' | 'high';
  estimated_hours: number;
  start_date: string;
  due_date: string;
  duration_days: number;
  success_criteria: string;
  suggested_position: number;
}

interface GeneratedPlan {
  plan_window: {
    start_date: string;
    end_date: string;
    weeks: number;
  };
  first_irreversible_step: {
    title: string;
    rationale: string;
  };
  tasks: GeneratedTask[];
}

interface PlanIntakeInput {
  hours_per_week: number;
  idea_type: string;
  current_state: string;
  constraint_text: string;
  done_definition: string;
  target_date: string | null;
}

type PlanRequestStatus = 'pending' | 'ready' | 'failed';

interface PlanRequest {
  id: string;
  user_id: string;
  idea_id: string;
  request_id: string;
  status: PlanRequestStatus;
  content: GeneratedPlan | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_TIME_MS = 30000;

export function usePlanGeneration(ideaId: string | null) {
  const { user } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [generatedPlan, setGeneratedPlan] = useState<GeneratedPlan | null>(null);
  
  const pollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pollStartTimeRef = useRef<number>(0);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  // Cleanup polling and realtime
  const cleanup = useCallback(() => {
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Generate UUID for request
  const generateRequestId = (): string => {
    return crypto.randomUUID();
  };

  // Poll for plan request status
  const pollForStatus = useCallback(async (requestId: string): Promise<void> => {
    if (!ideaId || !user) return;

    const elapsed = Date.now() - pollStartTimeRef.current;
    if (elapsed >= MAX_POLL_TIME_MS) {
      setError('Plan generation timed out. Please try again.');
      setGenerating(false);
      cleanup();
      return;
    }

    try {
      const { data, error: fetchError } = await supabase
        .from('plan_requests')
        .select('*')
        .eq('request_id', requestId)
        .single();

      if (fetchError) {
        console.error('Error polling plan_requests:', fetchError);
        // Continue polling
        pollTimeoutRef.current = setTimeout(() => pollForStatus(requestId), POLL_INTERVAL_MS);
        return;
      }

      const request = data as unknown as PlanRequest;

      if (request.status === 'ready') {
        setGeneratedPlan(request.content);
        setGenerating(false);
        setError(null);
        toast.success('Plan generated');
        cleanup();
        return;
      }

      if (request.status === 'failed') {
        setError(request.error_message || 'Plan generation failed');
        setGenerating(false);
        cleanup();
        return;
      }

      // Still pending, continue polling
      pollTimeoutRef.current = setTimeout(() => pollForStatus(requestId), POLL_INTERVAL_MS);
    } catch (err) {
      console.error('Poll error:', err);
      pollTimeoutRef.current = setTimeout(() => pollForStatus(requestId), POLL_INTERVAL_MS);
    }
  }, [ideaId, user, cleanup]);

  // Subscribe to realtime changes
  const subscribeToChanges = useCallback((requestId: string) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    const channel = supabase
      .channel(`plan_request_${requestId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'plan_requests',
          filter: `request_id=eq.${requestId}`,
        },
        (payload) => {
          const request = payload.new as unknown as PlanRequest;
          
          if (request.status === 'ready') {
            setGeneratedPlan(request.content);
            setGenerating(false);
            setError(null);
            toast.success('Plan generated');
            cleanup();
          } else if (request.status === 'failed') {
            setError(request.error_message || 'Plan generation failed');
            setGenerating(false);
            cleanup();
          }
        }
      )
      .subscribe();

    channelRef.current = channel;
  }, [cleanup]);

  // Generate plan with the hardened flow
  const generatePlan = useCallback(async (
    idea: Idea,
    intake: PlanIntakeInput
  ): Promise<GeneratedPlan | null> => {
    if (!ideaId || !user) {
      toast.error('Please select an idea first.');
      return null;
    }

    // Reset state
    setGenerating(true);
    setError(null);
    setGeneratedPlan(null);
    cleanup();

    const requestId = generateRequestId();
    setCurrentRequestId(requestId);

    try {
      // Step 1: Insert pending plan request
      const { error: insertError } = await supabase
        .from('plan_requests')
        .insert({
          user_id: user.id,
          idea_id: ideaId,
          request_id: requestId,
          status: 'pending',
        });

      if (insertError) {
        console.error('Failed to create plan request:', insertError);
        throw new Error('Failed to initiate plan generation');
      }

      // Step 2: Start polling and realtime subscription
      pollStartTimeRef.current = Date.now();
      subscribeToChanges(requestId);
      pollTimeoutRef.current = setTimeout(() => pollForStatus(requestId), POLL_INTERVAL_MS);

      // Step 3: Fetch recent updates for context
      const { data: recentUpdates } = await supabase
        .from('idea_updates')
        .select('reflection, created_at')
        .eq('idea_id', ideaId)
        .order('created_at', { ascending: false })
        .limit(3);

      // Step 4: Call edge function (fire and forget - polling will handle result)
      const { data, error: invokeError } = await supabase.functions.invoke('generate-plan', {
        body: {
          idea: {
            title: idea.title,
            one_liner: idea.one_liner,
            problem: idea.problem,
            audience: idea.audience,
            proposed_solution: idea.proposed_solution,
            differentiator: idea.differentiator,
          },
          intake: {
            hours_per_week: intake.hours_per_week,
            idea_type: intake.idea_type,
            current_state: intake.current_state,
            constraint_text: intake.constraint_text,
            done_definition: intake.done_definition,
            target_date: intake.target_date,
          },
          recentUpdates: recentUpdates || [],
          request_id: requestId,
          idea_id: ideaId,
        },
      });

      // If edge function returns immediately with a result, use it
      if (!invokeError && data && data.tasks) {
        cleanup();
        setGeneratedPlan(data as GeneratedPlan);
        setGenerating(false);
        setError(null);
        toast.success('Plan generated');
        return data as GeneratedPlan;
      }

      if (invokeError) {
        console.error('Edge function error:', invokeError);
        
        if (invokeError.message?.includes('429')) {
          toast.error('Rate limit exceeded. Please try again in a moment.');
        } else if (invokeError.message?.includes('402')) {
          toast.error('AI credits exhausted. Please add credits to continue.');
        }
        
        // Still let polling check for fallback plan
      }

      // Return null - polling will update state when ready
      return null;
    } catch (err) {
      console.error('generatePlan error:', err);
      setGenerating(false);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      cleanup();
      toast.error('Failed to generate plan. Please try again.');
      return null;
    }
  }, [ideaId, user, cleanup, subscribeToChanges, pollForStatus]);

  // Retry with new request ID
  const retry = useCallback(async (
    idea: Idea,
    intake: PlanIntakeInput
  ): Promise<GeneratedPlan | null> => {
    return generatePlan(idea, intake);
  }, [generatePlan]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    generating,
    error,
    generatedPlan,
    currentRequestId,
    generatePlan,
    retry,
    clearError,
    cleanup,
  };
}
